import {getAllQuartersOfCity} from "../../gameState/getAllQuartersOfCity";
import {getNeighborHexKeys} from "../botGrid";
import {NEUTRAL_PLAYER_ID} from "../../../constants/players";
import {keyToCoords} from "../../coords/keyToCoords";

// Гексагональная дистанция по осевым координатам (для радиусов защиты/агрессии)
const calcHexDistance = (cellKeyA, cellKeyB) => {
  const [aq, ar] = keyToCoords(cellKeyA);
  const [bq, br] = keyToCoords(cellKeyB);
  return (Math.abs(aq - bq) + Math.abs(ar - br) + Math.abs((aq + ar) - (bq + br))) / 2;
};

/**
 * Суммарный размер армии игрока (простая мера военной силы).
 */
export const calcPlayerArmyStrength = (playerId, units) => {
  return units
    .filter((unit) => unit.ownerId === playerId)
    .reduce((sum, unit) => sum + unit.size, 0);
};

/**
 * Максимальная армия среди противников (для сравнения баланса сил).
 */
export const calcStrongestOpponentArmy = (currentPlayerId, players, units) => {
  let strongest = 0;
  players.forEach((player) => {
    if (player.id === currentPlayerId) return;
    strongest = Math.max(strongest, calcPlayerArmyStrength(player.id, units));
  });
  return strongest;
};

/**
 * Гарнизон города: суммарный размер отрядов владельца на кварталах города и смежно им.
 */
export const calcCityGarrisonStrength = (city, ownerId, units, gridRenderer) => {
  const cityQuarters = getAllQuartersOfCity(city);
  const defenseCells = new Set(cityQuarters);
  cityQuarters.forEach((quarterHexKey) => {
    getNeighborHexKeys(quarterHexKey, gridRenderer).forEach((adjKey) => defenseCells.add(adjKey));
  });

  return units
    .filter((unit) => unit.ownerId === ownerId && defenseCells.has(unit.hexKey))
    .reduce((sum, unit) => sum + unit.size, 0);
};

/**
 * Защита города по живому тесту 16.3: сумма размеров отрядов владельца
 * в радиусе 3 от центрального квартала города.
 */
export const calcCityDefenseNearCenter = (city, ownerId, units) => {
  return units
    .filter((unit) => unit.ownerId === ownerId
      && calcHexDistance(unit.hexKey, city.centralQuarter) <= 3)
    .reduce((sum, unit) => sum + unit.size, 0);
};

/**
 * Цели поздней агрессии (живой тест 16.3): города противников/нейтралов в
 * пределах maxDistance от отрядов бота, чья защита у центра меньше суммарной
 * силы отрядов бота в maxDistance от центра города.
 * Возвращает [{city, color, defense}].
 */
export const findLateAggressionTargets = (currentPlayer, units, citiesCache, players, maxDistance) => {
  const targets = [];
  const myUnits = units.filter((unit) => unit.ownerId === currentPlayer.id);
  if (myUnits.length === 0) {
    return targets;
  }

  Object.entries(citiesCache || {}).forEach(([color, cities]) => {
    if (color === currentPlayer.color) {
      return;
    }
    const owner = players.find((player) => player.color === color);
    const ownerId = owner ? owner.id : NEUTRAL_PLAYER_ID;

    (cities || []).forEach((city) => {
      const nearbyAttackStrength = myUnits
        .filter((unit) => calcHexDistance(unit.hexKey, city.centralQuarter) <= maxDistance)
        .reduce((sum, unit) => sum + unit.size, 0);
      if (nearbyAttackStrength === 0) {
        return;
      }
      const defense = calcCityDefenseNearCenter(city, ownerId, units);
      if (defense < nearbyAttackStrength) {
        targets.push({city, color, defense});
      }
    });
  });

  return targets;
};

/**
 * Слабые города противников (включая нейтральные): гарнизон меньше атакующей силы бота.
 * Возвращает [{city, color, garrison, quarters}] по возрастанию гарнизона.
 */
export const findWeakEnemyCities = (state, currentPlayer, units, citiesCache, players, gridRenderer, attackStrength) => {
  const weakCities = [];

  Object.entries(citiesCache || {}).forEach(([color, cities]) => {
    if (color === currentPlayer.color) return;
    const owner = players.find((player) => player.color === color);
    const ownerId = owner ? owner.id : 'neutral_id';

    cities.forEach((city) => {
      const garrison = calcCityGarrisonStrength(city, ownerId, units, gridRenderer);
      if (garrison < attackStrength) {
        weakCities.push({city, color, garrison, quarters: getAllQuartersOfCity(city)});
      }
    });
  });

  return weakCities.sort((a, b) => a.garrison - b.garrison);
};

/**
 * Свои города под угрозой: враг на квартале или в 2 ходах от кварталов города.
 */
export const findOwnCitiesUnderThreat = (currentPlayer, units, citiesCache, gridRenderer) => {
  const threatened = [];
  const cities = citiesCache?.[currentPlayer.color] || [];

  cities.forEach((city) => {
    const cityQuarters = getAllQuartersOfCity(city);
    const nearCells = new Set(cityQuarters);
    cityQuarters.forEach((quarterHexKey) => {
      getNeighborHexKeys(quarterHexKey, gridRenderer).forEach((adjKey) => {
        nearCells.add(adjKey);
        getNeighborHexKeys(adjKey, gridRenderer).forEach((adj2Key) => nearCells.add(adj2Key));
      });
    });

    // Нейтральные отряды статичны и никогда не нападают — угрозой городу не являются
    const enemyStrengthNear = units
      .filter((unit) => unit.ownerId !== currentPlayer.id
        && unit.ownerId !== NEUTRAL_PLAYER_ID
        && nearCells.has(unit.hexKey))
      .reduce((sum, unit) => sum + unit.size, 0);

    if (enemyStrengthNear > 0) {
      const garrison = calcCityGarrisonStrength(city, currentPlayer.id, units, gridRenderer);
      threatened.push({city, enemyStrengthNear, garrison, quarters: cityQuarters});
    }
  });

  return threatened;
};
